import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { AUTH_STATE_TOKEN } from '~console/app/~shared/constants/global.constants';
import { BaseAuthComponent } from '../../abstract-components/base-auth.component';
import { ROUTES } from '~console/app/~shared/constants/routes.constants';
import { haveLeastOneFieldFilled } from '~core/utils/form';
import { DialogService } from '~console/app/~shared/services/dialog.service';
import { AdminMigrationModalComponent } from '../../modals/admin-migration-modal/admin-migration-modal.component';
import { CompanyUserMigrationModalComponent } from '../../modals/company-user-migration-modal/company-user-migration-modal.component';

@Component({
  selector: 'login-form',
  standalone: false,
  templateUrl: './login-form.component.html',
  styleUrl: './login-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginFormComponent
  extends BaseAuthComponent
  implements AfterViewInit
{
  readonly isLoggingIn$ = this._authFacade.isLoggingIn$;
  readonly statePropertyPath = `${AUTH_STATE_TOKEN.getName()}.login.loginFormValues`;
  private _dialogService = inject(DialogService);

  routes = ROUTES;
  loginForm = new FormGroup(
    {
      username: new FormControl('', {
        validators: [Validators.required, Validators.email],
        nonNullable: true
      }),
      password: new FormControl('', {
        validators: [Validators.minLength(8), Validators.required],
        nonNullable: true
      }),
      accessTokenId: new FormControl('', {
        validators: [],
        nonNullable: true
      }),
      organizationIdToConnectTo: new FormControl('', {
        validators: [],
        nonNullable: true
      })
    },
    { validators: [haveLeastOneFieldFilled(['password', 'accessTokenId'])] }
  );

  ngAfterViewInit(): void {
    const { accessTokenId, username, organizationId } =
      this._globalFacade.queryParams || {};
    if (accessTokenId && username) {
      this.loginForm.patchValue({
        accessTokenId,
        username,
        organizationIdToConnectTo: organizationId
      });
      setTimeout(() => {
        this._authFacade.loginWithAccessToken();
      }, 500);
      return;
    }
    this.loginForm.patchValue({
      accessTokenId: '',
      organizationIdToConnectTo: ''
    });
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }
    const { accessTokenId } = this.loginForm.value;
    if (accessTokenId) {
      this._authFacade.loginWithAccessToken();
      return;
    }
    this._authFacade.loginWithPassword();
  }

  openAdminMigrationModal(): void {
    this._dialogService.open({
      component: AdminMigrationModalComponent,
      config: {
        panelClass: ['mat-dialog-custom'],
        disableClose: true,
        autoFocus: false,
        maxWidth: '750px'
      }
    });
  }

  openCompanyUserMigrationModal(): void {
    this._dialogService.open({
      component: CompanyUserMigrationModalComponent,
      config: {
        panelClass: ['mat-dialog-custom'],
        disableClose: true,
        autoFocus: false,
        maxWidth: '750px'
      }
    });
  }

  get username(): AbstractControl<string | null> | null {
    return this.loginForm.get('username');
  }

  get password(): AbstractControl<string | null> | null {
    return this.loginForm.get('password');
  }
}
